import React from "react";

/**
 * Drawer — right-side slide-over panel (Phase 2c Containers).
 *
 * Distinct from Modal: drawers anchor to a screen edge, hold richer
 * tool-like content (the assumptions panel, a filter sidebar, a
 * detail flyout), and don't demand a confirm/cancel decision the way
 * a modal does. Audit §3.4: today's InputsPanel is the only consumer
 * pattern and implements its own slide-over markup + focus trap. This
 * primitive consolidates that.
 *
 * Props:
 *   open       bool
 *   onClose    () => void
 *   title      ReactNode header title (optional — pass null for headerless)
 *   side       "right" (default) | "left"
 *   width      "sm" (320) | "md" (clamp 320-28vw-420, default) | "lg" (480)
 *              | string  — any CSS dimension overrides the preset
 *   children   panel body (caller composes Form / sections / etc.)
 *
 * A11y:
 *   - role="dialog", aria-modal="true", aria-labelledby wired to the
 *     header if title is set.
 *   - Focus trap mirrors the Modal implementation (Tab cycles inside,
 *     Shift+Tab cycles back, restores focus on close).
 *   - Escape closes.
 *   - Outside click closes (the backdrop swallows it). Pass
 *     dismissOnOutsideClick={false} for forms with unsaved state.
 *
 * Animation:
 *   - 240ms slide + opacity. CSS keyframes scoped to .v2-drawer (no React
 *     state machine; CSS handles enter/exit via the `open` boolean +
 *     a brief pre-mount).
 */

const { useState, useEffect, useRef } = React;

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  side?: "left" | "right";
  width?: "sm" | "md" | "lg" | string;
  /** Default: true. Set false for forms with unsaved state. */
  dismissOnOutsideClick?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export default function Drawer({
  open,
  onClose,
  title,
  side = "right",
  width = "md",
  dismissOnOutsideClick = true,
  className = "",
  children,
}: DrawerProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus trap — same shape as Modal's. Restores focus on close.
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const container = dialogRef.current;
    const prevFocus = document.activeElement;
    const focusables = (): HTMLElement[] => Array.from(
      container.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
      )
    );
    const first = focusables()[0];
    first?.focus?.();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const els = focusables();
      if (!els.length) { e.preventDefault(); return; }
      const f = els[0], l = els[els.length - 1];
      if (e.shiftKey && document.activeElement === f) { e.preventDefault(); l.focus(); }
      else if (!e.shiftKey && document.activeElement === l) { e.preventDefault(); f.focus(); }
    };
    container.addEventListener("keydown", onKey);
    return () => {
      container.removeEventListener("keydown", onKey);
      try { (prevFocus as HTMLElement)?.focus?.(); } catch {}
    };
  }, [open]);

  if (!open) return null;

  const isPreset = width === "sm" || width === "md" || width === "lg";
  const inlineStyle: React.CSSProperties = isPreset
    ? {}
    : { width: width as string };
  const titleId = title ? "v2-drawer-title" : undefined;

  return (
    <div
      className="v2-drawer-backdrop"
      onClick={dismissOnOutsideClick ? onClose : undefined}
    >
      <aside
        ref={dialogRef}
        className={[
          "v2-drawer",
          `v2-drawer--${side}`,
          isPreset ? `v2-drawer--width-${width}` : "",
          className,
        ].filter(Boolean).join(" ")}
        style={inlineStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {title != null ? (
          <header className="v2-drawer__header">
            <h2 id={titleId} className="v2-drawer__title">{title}</h2>
            <button
              type="button"
              className="v2-drawer__close"
              aria-label="Close drawer"
              onClick={onClose}
            >
              ×
            </button>
          </header>
        ) : null}
        <div className="v2-drawer__body">{children}</div>
      </aside>
    </div>
  );
}
